<script setup>
// Summary above the analytics charts (docs/analytics/SPEC.md, decision 12): value, profit, passive income.
import { computed } from 'vue';
import { t } from '../i18n';
import { formatMoney, formatSignedMoney, formatSignedPercent, formatUnsignedPercent, signClass } from '../format';
import { passiveIncomeYear, portfolioTotals } from '../portfolioView';
import { isPhone } from '../isPhone';
import InfoHint from './InfoHint.vue';

const props = defineProps({
  portfolio: {
    type: Object,
    required: true,
  },
  livePrices: {
    type: Object,
    default: () => ({}),
  },
  fxRates: {
    type: Object,
    default: () => ({}),
  },
  // Calendar items for the next 12 months of this view (GET /cashflow/calendar)
  forwardPayouts: {
    type: Array,
    default: () => [],
  },
  // Value of currency and cash positions, RUB (from the allocation)
  cashValue: {
    type: Number,
    default: 0,
  },
});

const totals = computed(() => portfolioTotals(props.portfolio, props.livePrices, props.fxRates));

// Dividends and coupons with a known amount, as "Total" in the calendar for 12 months, after tax.
const passiveYear = computed(() => passiveIncomeYear(props.forwardPayouts));

const passiveOnValue = computed(() => (totals.value.total ? (passiveYear.value / totals.value.total) * 100 : null));
const passiveOnInvested = computed(() => (totals.value.invested ? (passiveYear.value / totals.value.invested) * 100 : null));
</script>

<template>
  <!-- Phone: one card, values in rows (docs/design/SPEC.md, «Аналитика») -->
  <section
    v-if="isPhone"
    class="rounded-3xl bg-slate-900 p-5"
  >
    <p class="text-[0.8125rem] text-slate-500">
      {{ t('summary.value') }}
    </p>
    <p class="mt-0.5 text-[1.75rem] font-semibold leading-9 tabular-nums text-slate-100">
      {{ formatMoney(totals.total, portfolio.currency) }}
    </p>
    <p class="mt-0.5 text-[0.8125rem] tabular-nums text-slate-400">
      {{ t('summary.invested', { amount: formatMoney(totals.invested, portfolio.currency) }) }}
    </p>
    <div
      class="my-4 h-px bg-slate-800"
      aria-hidden="true"
    />
    <dl class="text-[0.9375rem] tabular-nums">
      <div class="flex min-h-11 items-center justify-between gap-3">
        <dt class="text-slate-400">
          {{ t('summary.profit') }}
        </dt>
        <dd
          class="text-right"
          :class="signClass(totals.yieldAbs)"
        >
          {{ formatSignedMoney(totals.yieldAbs, portfolio.currency) }} · {{ formatSignedPercent(totals.yieldPercent) }}
        </dd>
      </div>
      <div
        v-if="totals.daily !== null"
        class="flex min-h-11 items-center justify-between gap-3"
      >
        <dt class="text-slate-400">
          {{ t('summary.perDay') }}
        </dt>
        <dd
          class="text-right"
          :class="signClass(totals.daily)"
        >
          {{ formatSignedMoney(totals.daily, portfolio.currency) }} · {{ formatSignedPercent(totals.dailyPercent) }}
        </dd>
      </div>
      <div class="flex min-h-11 items-center justify-between gap-3">
        <dt class="text-slate-400">
          {{ t('summary.passive') }}
        </dt>
        <dd class="text-right">
          <span class="block text-slate-100">{{ formatUnsignedPercent(passiveOnValue) }} · {{ t('summary.perYear', { amount: formatMoney(passiveYear, 'RUB') }) }}</span>
          <span
            v-if="passiveOnInvested !== null"
            class="block text-[0.75rem] text-slate-500"
          >{{ t('summary.onInvested', { percent: formatUnsignedPercent(passiveOnInvested) }) }}</span>
        </dd>
      </div>
    </dl>
  </section>
  <div
    v-else
    class="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4"
  >
    <section class="col-span-2 rounded-lg border border-slate-700 bg-slate-900/40 p-4 sm:p-5 md:col-span-1">
      <h3 class="flex items-center gap-2 text-[0.8125rem] font-semibold text-slate-200">
        <span
          class="hidden size-6 items-center justify-center rounded-md bg-sky-500/15 text-sky-400 sm:flex"
          aria-hidden="true"
        >
          <svg
            class="size-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          ><path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 7h18v12H3zM3 7l2-3h14l2 3M16 13h2"
          /></svg>
        </span>
        {{ t('summary.value') }}
        <InfoHint :label="t('summary.value')">
          <p>{{ t('summary.valueHint') }}</p>
          <p class="mt-2 border-t border-slate-700 pt-2 tabular-nums">
            {{ t('summary.assetsValue') }}: {{ formatMoney(totals.total === null ? null : totals.total - cashValue, 'RUB') }}<br>
            {{ t('summary.cashValue') }}: {{ formatMoney(cashValue, 'RUB') }}
          </p>
        </InfoHint>
      </h3>
      <p class="mt-2 text-2xl font-semibold tabular-nums text-slate-100 md:text-xl">
        {{ formatMoney(totals.total, portfolio.currency) }}
      </p>
      <p class="mt-1 text-xs tabular-nums text-slate-400">
        {{ t('summary.invested', { amount: formatMoney(totals.invested, portfolio.currency) }) }}
      </p>
    </section>

    <section class="min-w-0 rounded-lg border border-slate-700 bg-slate-900/40 p-3.5 sm:p-5">
      <h3 class="flex items-center gap-2 text-[0.8125rem] font-semibold text-slate-200">
        <span
          class="hidden size-6 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-400 sm:flex"
          aria-hidden="true"
        >
          <svg
            class="size-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          ><path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 17l6-6 4 4 8-8M15 7h6v6"
          /></svg>
        </span>
        {{ t('summary.profit') }}
        <InfoHint :label="t('summary.profit')">
          {{ t('summary.profitHint') }}
        </InfoHint>
      </h3>
      <p class="mt-2 flex flex-wrap items-baseline gap-x-2 tabular-nums">
        <span
          class="text-lg font-semibold sm:text-xl"
          :class="signClass(totals.yieldAbs)"
        >{{ formatSignedMoney(totals.yieldAbs, portfolio.currency) }}</span>
        <span
          class="text-xs"
          :class="signClass(totals.yieldPercent)"
        >{{ formatSignedPercent(totals.yieldPercent) }}</span>
      </p>
      <p
        v-if="totals.daily !== null"
        class="mt-1 text-xs tabular-nums"
      >
        <span class="text-slate-400">{{ t('summary.perDay') }}</span>
        <span
          class="ml-1 inline-block whitespace-nowrap"
          :class="signClass(totals.daily)"
        >{{ formatSignedMoney(totals.daily, portfolio.currency) }} · {{ formatSignedPercent(totals.dailyPercent) }}</span>
      </p>
    </section>

    <section class="min-w-0 rounded-lg border border-slate-700 bg-slate-900/40 p-3.5 sm:p-5">
      <h3 class="flex items-center gap-2 text-[0.8125rem] font-semibold text-slate-200">
        <span
          class="hidden size-6 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-400 sm:flex"
          aria-hidden="true"
        >
          <svg
            class="size-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          ><path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 3v18M17 7.5C17 5.6 14.8 4.5 12 4.5S7 5.6 7 7.5 9.2 10.3 12 11s5 1.6 5 3.8-2.2 3.2-5 3.2-5-1.2-5-3"
          /></svg>
        </span>
        {{ t('summary.passive') }}
        <InfoHint
          :label="t('summary.passive')"
          align="right"
        >
          {{ t('summary.passiveHint') }}
        </InfoHint>
      </h3>
      <p class="mt-2 flex flex-wrap items-baseline gap-x-2 tabular-nums">
        <span class="text-lg font-semibold text-slate-100 sm:text-xl">{{ formatUnsignedPercent(passiveOnValue) }}</span>
        <span
          v-if="passiveOnInvested !== null"
          class="text-xs text-slate-400"
        >{{ t('summary.onInvested', { percent: formatUnsignedPercent(passiveOnInvested) }) }}</span>
      </p>
      <p class="mt-1 text-xs tabular-nums text-slate-400">
        {{ t('summary.perYear', { amount: formatMoney(passiveYear, 'RUB') }) }}
      </p>
    </section>
  </div>
</template>
